import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../store/types';
import CustomButton from '../../buttons/CustomButton';
import { ButtonTypes } from '../../../services/exports/Interfaces';
import { PaymentGateway, requiresTaxpayerDocument } from '../../../services/exports/Constants';
import { ChildPayButtonProps } from './types';
import type { CheckoutFunction } from '../../../hooks/payment/useOrderCheckout';
import { PaymentReturnUrlPlaceholder } from '../../../hooks/payment/useCheckout';
import useSpreedlyOffsitePayment, { withOffsiteReturnParams } from '../../../hooks/payment/useSpreedlyOffsitePayment';
import SpreedlyStripeApmSheet from '../spreedly/SpreedlyStripeApmSheet';
import SpreedlyBraintreeSheet from '../spreedly/SpreedlyBraintreeSheet';
import SpreedlyQrSheet from '../spreedly/SpreedlyQrSheet';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import { cpfDigits, isValidCpf } from '../../../utils/cpf';

/**
 * Spreedly offsite checkout (iDEAL, Bancontact, PayPal, …). Nothing is tokenized here: the method
 * is described server-side, so the order carries only the return URL. The backend answers 402 with
 * the next step: the customer leaves for their bank or PayPal — straight away, or after Stripe's
 * Payment Element — and settles on the way back, or presses a Braintree wallet button and settles
 * without leaving.
 */
export default function SpreedlyOffsitePayButton({
  title,
  loading,
  disabled,
  silentlyDisabled,
  setLoading,
  onClick,
  checkout,
  generateReturnUrl,
  className,
}: ChildPayButtonProps) {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { confirmPayment, sheet, cancel, redirecting, braintreeSheet, confirmWallet, qrSheet, settleQr } =
    useSpreedlyOffsitePayment(company?.id, {
      awaitingAnswerUrl: (orderUuid, transactionToken) =>
        `${offsiteReturnUrl().replaceAll(PaymentReturnUrlPlaceholder.OrderUUID, orderUuid)}&transaction_token=${encodeURIComponent(transactionToken)}`,
      onNavigationRefused: () => setLoading(false),
    });
  const { selectedPaymentMethod, customerDocument, setCustomerDocument } = usePaymentFlow();

  // Pix and NuPay cannot start without the payer's CPF.
  const needsDocument = requiresTaxpayerDocument(selectedPaymentMethod);
  const documentMissing = needsDocument && !isValidCpf(customerDocument);
  const inFlightRef = useRef(false);

  // Back from the bank through the browser's back-forward cache: the page comes back exactly as
  // it left, spinner and in-flight guard included, and the checkout promise they wait on never
  // resolves. The customer is here to pay again, so both let go.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        inFlightRef.current = false;
        setLoading(false);
      }
    };

    window.addEventListener('pageshow', onPageShow);

    return () => window.removeEventListener('pageshow', onPageShow);
  }, [setLoading]);

  const offsiteReturnUrl = () =>
    withOffsiteReturnParams(
      generateReturnUrl({ [PaymentReturnUrlPlaceholder.PaymentGateway]: PaymentGateway.Spreedly }),
      PaymentReturnUrlPlaceholder.OrderUUID,
    );

  const _onClick = async () => {
    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    setLoading(true);

    try {
      const returnUrl = offsiteReturnUrl();

      const customCheckout: CheckoutFunction = (confirmFn, paymentIntent, extraData, extraHeaders, options) =>
        checkout(
          confirmFn,
          paymentIntent,
          {
            ...extraData,
            return_url: returnUrl,
            ...(needsDocument ? { customer_document: cpfDigits(customerDocument) } : {}),
          },
          extraHeaders,
          options,
        );

      // From here checkout() owns `loading`: a started payment leaves the page, a failed or
      // cancelled one clears it through onPaymentError.
      const paid = await onClick(confirmPayment, undefined, customCheckout);

      // Sent once; a paid checkout has no further use for the CPF.
      if (paid && needsDocument) {
        setCustomerDocument('');
      }
    } finally {
      inFlightRef.current = false;
    }
  };

  return (
    <>
      {sheet && <SpreedlyStripeApmSheet sheet={sheet} onCancel={cancel} onRedirecting={redirecting} />}
      {braintreeSheet && <SpreedlyBraintreeSheet sheet={braintreeSheet} onResult={confirmWallet} onCancel={cancel} />}
      {qrSheet && <SpreedlyQrSheet sheet={qrSheet} onSettled={settleQr} onCancel={cancel} />}
      <span
        data-test="pay-button-state"
        data-ready={!disabled && !documentMissing && !silentlyDisabled && !loading ? 'true' : 'false'}
        className="hidden"
      />
      <CustomButton
        buttonType={ButtonTypes.primary}
        title={title}
        disabled={disabled || documentMissing}
        silentlyDisabled={silentlyDisabled}
        onClick={_onClick}
        loading={loading}
        showArrow
        className={className}
        testId="pay-button"
      />
    </>
  );
}
